import { useState, type FormEvent } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { authRecuperacaoApi } from '../../api/endpoints'
import { Input, PasswordInput } from '../../components/ui/Input'
import { Button } from '../../components/ui/Button'
import logoLeao from '../../assets/logo-leao.png'

function Moldura({ titulo, subtitulo, children }: { titulo: string; subtitulo: string; children: React.ReactNode }) {
  return (
    <div className="relative flex min-h-screen flex-col bg-bg">
      <div
        className="absolute inset-x-0 top-0 h-[380px] bg-sidebar"
        style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, rgba(255,179,0,0.18), transparent 45%), radial-gradient(circle at 85% 0%, rgba(255,106,0,0.14), transparent 40%)' }}
        aria-hidden
      />
      <div className="relative flex flex-1 flex-col items-center px-4 pt-14 pb-8">
        <div className="mb-8 text-center">
          <div className="inline-flex flex-col items-center gap-2">
            <img src={logoLeao} alt="" className="h-24 w-auto" />
            <span className="text-xl font-bold tracking-[0.2em] text-white">WALLEON</span>
          </div>
        </div>
        <div className="w-full max-w-md rounded bg-surface p-6 shadow-dropdown sm:p-8">
          <div className="mb-6 text-center">
            <h5 className="text-xl font-semibold text-primary">{titulo}</h5>
            <p className="mt-1 text-13 text-muted">{subtitulo}</p>
          </div>
          {children}
        </div>
      </div>
    </div>
  )
}

// A mensagem de sucesso é sempre a mesma, venha ou não um e-mail cadastrado — o backend nunca revela
// se o endereço existe, então não há nada de específico para mostrar aqui além da confirmação de envio.
export function EsqueciSenhaPage() {
  const [email, setEmail] = useState('')
  const [enviado, setEnviado] = useState(false)
  const [loading, setLoading] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setErro(null)
    setLoading(true)
    try {
      await authRecuperacaoApi.esqueciSenha(email)
      setEnviado(true)
    } catch (e) {
      setErro((e as Error).message || 'Não foi possível enviar o link. Tente de novo em instantes.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Moldura titulo="Esqueceu sua senha?" subtitulo="Informe seu e-mail e enviamos um link para redefinir.">
      {enviado ? (
        <div className="space-y-4 text-center">
          <div className="flex items-center gap-2 rounded bg-income/10 px-3 py-2.5 text-13 text-income" role="status">
            <i className="ri-mail-check-line" aria-hidden /> Se esse e-mail estiver cadastrado, você vai receber um link de redefinição em instantes.
          </div>
          <Link to="/login" className="text-13 text-primary hover:underline">Voltar para o login</Link>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="E-mail cadastrado"
            type="email"
            placeholder="seu@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
          />
          {erro && (
            <div className="flex items-center gap-2 rounded bg-expense/10 px-3 py-2.5 text-13 text-expense" role="alert">
              <i className="ri-error-warning-line" aria-hidden /> {erro}
            </div>
          )}
          <Button type="submit" loading={loading} className="w-full">Enviar link de redefinição</Button>
          <p className="text-center text-13 text-muted">
            <Link to="/login" className="text-primary hover:underline">Voltar para o login</Link>
          </p>
        </form>
      )}
    </Moldura>
  )
}

export function RedefinirSenhaPage() {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''

  const [novaSenha, setNovaSenha] = useState('')
  const [confirmar, setConfirmar] = useState('')
  const [concluido, setConcluido] = useState(false)
  const [loading, setLoading] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setErro(null)

    if (novaSenha.length < 8) { setErro('A senha precisa ter pelo menos 8 caracteres.'); return }
    if (novaSenha !== confirmar) { setErro('As senhas não são iguais.'); return }

    setLoading(true)
    try {
      await authRecuperacaoApi.redefinirSenha(token, novaSenha)
      setConcluido(true)
    } catch (e) {
      setErro((e as Error).message || 'Esse link é inválido ou já expirou. Peça um novo.')
    } finally {
      setLoading(false)
    }
  }

  if (!token) {
    return (
      <Moldura titulo="Link inválido" subtitulo="Esse link de redefinição está incompleto.">
        <div className="space-y-4 text-center">
          <div className="flex items-center gap-2 rounded bg-expense/10 px-3 py-2.5 text-13 text-expense" role="alert">
            <i className="ri-error-warning-line" aria-hidden /> Abra o link exatamente como veio no e-mail, ou peça um novo.
          </div>
          <Link to="/esqueci-senha" className="text-13 text-primary hover:underline">Pedir novo link</Link>
        </div>
      </Moldura>
    )
  }

  return (
    <Moldura titulo="Redefinir senha" subtitulo="Escolha uma nova senha para sua conta.">
      {concluido ? (
        <div className="space-y-4 text-center">
          <div className="flex items-center gap-2 rounded bg-income/10 px-3 py-2.5 text-13 text-income" role="status">
            <i className="ri-check-line" aria-hidden /> Senha redefinida. Suas outras sessões foram encerradas por segurança.
          </div>
          <Link to="/login"><Button className="w-full">Entrar com a nova senha</Button></Link>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <PasswordInput
            label="Nova senha"
            placeholder="Pelo menos 8 caracteres"
            value={novaSenha}
            onChange={(e) => setNovaSenha(e.target.value)}
            required
            autoComplete="new-password"
          />
          <PasswordInput
            label="Confirmar nova senha"
            value={confirmar}
            onChange={(e) => setConfirmar(e.target.value)}
            required
            autoComplete="new-password"
          />
          {erro && (
            <div className="flex items-center gap-2 rounded bg-expense/10 px-3 py-2.5 text-13 text-expense" role="alert">
              <i className="ri-error-warning-line" aria-hidden /> {erro}
            </div>
          )}
          <Button type="submit" loading={loading} className="w-full">Redefinir senha</Button>
        </form>
      )}
    </Moldura>
  )
}
